import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/i18n";
import { AuthProvider, useAuth, canModule, isAdminRole } from "@/auth";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Contacts from "@/pages/Contacts";
import Documents from "@/pages/Documents";
import DocumentForm from "@/pages/DocumentForm";
import DocumentDetail from "@/pages/DocumentDetail";
import Settings from "@/pages/Settings";
import ContactCard from "@/pages/ContactCard";
import Payroll from "@/pages/Payroll";
import MyData from "@/pages/MyData";
import Ledger from "@/pages/Ledger";
import UsersPage from "@/pages/Users";
import Brand from "@/pages/Brand";

const firstAllowed = (u) => ["dashboard", "documents", "contacts", "payroll", "mydata", "ledger", "settings"].find((m) => canModule(u, m));
const PATHS = { dashboard: "/", documents: "/documents", contacts: "/contacts", payroll: "/payroll", mydata: "/mydata", ledger: "/ledger", settings: "/settings" };

function Protected() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm" data-testid="auth-loading">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout />;
}

function Guard({ module, children }) {
  const { user } = useAuth();
  if (canModule(user, module)) return children;
  const m = firstAllowed(user);
  return <Navigate to={m ? PATHS[m] : "/login"} replace />;
}

function AdminGuard({ children }) {
  const { user } = useAuth();
  return isAdminRole(user) ? children : <Navigate to="/" replace />;
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<Protected />}>
              <Route path="/" element={<Guard module="dashboard"><Dashboard /></Guard>} />
              <Route path="/contacts" element={<Guard module="contacts"><Contacts /></Guard>} />
              <Route path="/contacts/:id" element={<Guard module="contacts"><ContactCard /></Guard>} />
              <Route path="/payroll" element={<Guard module="payroll"><Payroll /></Guard>} />
              <Route path="/mydata" element={<Guard module="mydata"><MyData /></Guard>} />
              <Route path="/ledger" element={<Guard module="ledger"><Ledger /></Guard>} />
              <Route element={<Guard module="documents"><Outlet /></Guard>}>
                <Route path="/documents" element={<Documents />} />
                <Route path="/documents/new" element={<DocumentForm />} />
                <Route path="/documents/:id/edit" element={<DocumentForm />} />
                <Route path="/documents/:id" element={<DocumentDetail />} />
              </Route>
              <Route path="/settings" element={<Guard module="settings"><Settings /></Guard>} />
              <Route path="/users" element={<AdminGuard><UsersPage /></AdminGuard>} />
              <Route path="/brand" element={<AdminGuard><Brand /></AdminGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </LanguageProvider>
  );
}

export default App;
